import { useQuery } from "@tanstack/react-query";
import { useRoute, useLocation } from "wouter";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, User, Phone, MapPin, Truck, Info, Calendar, Mail } from "lucide-react";

function InfoRow({ label, value }: { label: string; value: string | null | undefined }) {
  if (!value) return null;
  return (
    <div className="flex flex-col gap-0.5" data-testid={`info-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <span className="text-xs text-muted-foreground font-medium">{label}</span>
      <span className="text-sm">{value}</span>
    </div>
  );
}

function formatAdditionalKey(key: string): string {
  return key
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase())
    .replace(/\bA1 /g, '')
    .replace(/\bCod /g, 'Cód. ')
    .replace(/\bDes /g, 'Desc. ');
}

export default function EntityDetailPage() {
  const [, navigate] = useLocation();
  const [match, params] = useRoute("/entities/:id");
  const id = params?.id;

  const { data: entity, isLoading, error } = useQuery({
    queryKey: ["/api/entities", id],
    queryFn: async () => {
      const response = await fetch(`/api/entities/${id}`);
      if (!response.ok) throw new Error("Entidade não encontrada");
      return response.json();
    },
    enabled: !!id,
  });

  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto py-12 text-center text-muted-foreground" data-testid="text-loading">
        Carregando...
      </div>
    );
  }

  if (error || !entity) {
    return (
      <div className="max-w-4xl mx-auto py-12 text-center space-y-4">
        <p className="text-muted-foreground" data-testid="text-error">Entidade não encontrada</p>
        <Button variant="outline" onClick={() => navigate("/entities")} data-testid="button-back-error">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Voltar
        </Button>
      </div>
    );
  }

  const additionalData = entity.additionalData && typeof entity.additionalData === 'object'
    ? Object.entries(entity.additionalData).filter(([, v]) => v !== null && v !== undefined && v !== '')
    : [];

  const hasDeliveryAddress = entity.deliveryAddress || entity.deliveryCity || entity.deliveryState;

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate("/entities")} data-testid="button-back">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Voltar
        </Button>
        <div className="flex-1">
          <h2 className="text-2xl font-bold" data-testid="text-entity-name">{entity.name || "Sem nome"}</h2>
          <div className="flex items-center gap-2 mt-1">
            {entity.category && <Badge variant="outline" data-testid="badge-category">{entity.category}</Badge>}
            {entity.segment && <Badge variant="secondary" data-testid="badge-segment">{entity.segment}</Badge>}
            {entity.entityType && <Badge data-testid="badge-type">{entity.entityType}</Badge>}
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <User className="w-4 h-4" />
              Identificação
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-2 gap-4">
            <InfoRow label="Nome" value={entity.name} />
            <InfoRow label="CPF/CNPJ" value={entity.cpfCnpj} />
            <InfoRow label="Código" value={entity.code} />
            <InfoRow label="Categoria" value={entity.category} />
            <InfoRow label="Segmento" value={entity.segment} />
            <InfoRow label="Segmento 2" value={entity.segment2} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Phone className="w-4 h-4" />
              Contato
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <InfoRow label="Telefone" value={entity.phone} />
              <InfoRow label="Celular" value={entity.cellphone} />
            </div>
            {entity.email && (
              <div className="flex items-center gap-2">
                <Mail className="w-3 h-3 text-muted-foreground" />
                <span className="text-sm text-primary" data-testid="text-email">{entity.email}</span>
              </div>
            )}
            {entity.extraPhones && entity.extraPhones.length > 0 && (
              <div>
                <span className="text-xs text-muted-foreground font-medium">Telefones Extras</span>
                <div className="flex flex-wrap gap-2 mt-1">
                  {entity.extraPhones.map((p: string, i: number) => (
                    <Badge key={i} variant="outline" className="font-mono text-xs" data-testid={`badge-phone-${i}`}>
                      {p}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              Endereço Principal
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <InfoRow label="Endereço" value={entity.address} />
            </div>
            <InfoRow label="Bairro" value={entity.neighborhood} />
            <InfoRow label="Cidade" value={entity.city} />
            <InfoRow label="UF" value={entity.state} />
            <InfoRow label="CEP" value={entity.cep} />
            <InfoRow label="Região" value={entity.region} />
          </CardContent>
        </Card>

        {hasDeliveryAddress && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Truck className="w-4 h-4" />
                Endereço de Entrega
              </CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <InfoRow label="Endereço" value={entity.deliveryAddress} />
              </div>
              <InfoRow label="Bairro" value={entity.deliveryNeighborhood} />
              <InfoRow label="Cidade" value={entity.deliveryCity} />
              <InfoRow label="UF" value={entity.deliveryState} />
              <InfoRow label="CEP" value={entity.deliveryCep} />
            </CardContent>
          </Card>
        )}

        {entity.birthDate && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Informações Pessoais
              </CardTitle>
            </CardHeader>
            <CardContent>
              <InfoRow label="Data de Nascimento" value={entity.birthDate} />
            </CardContent>
          </Card>
        )}

        {additionalData.length > 0 && (
          <Card className="md:col-span-2">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Info className="w-4 h-4" />
                Dados Adicionais
                <Badge variant="secondary" className="ml-auto">{additionalData.length} campos</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {additionalData.map(([key, value]) => (
                  <InfoRow key={key} label={formatAdditionalKey(key)} value={String(value)} />
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      <Card className="bg-muted/30">
        <CardContent className="py-4">
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <div className="flex gap-6">
              <span>ID: <span className="font-mono">{entity.id}</span></span>
              {entity.sourceFile && <span>Origem: {entity.sourceFile}</span>}
            </div>
            <div className="flex gap-6">
              {entity.importedAt && <span>Importado: {new Date(entity.importedAt).toLocaleDateString('pt-BR')}</span>}
              {entity.createdAt && <span>Criado: {new Date(entity.createdAt).toLocaleDateString('pt-BR')}</span>}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
